import { useLoaderData, useNavigate } from "react-router-dom";
import Recipe from "../../../models/recipe.model";
import { useState, useRef } from "react";
import {
    Card,
    CardContent,
    Typography,
    Switch,
    FormGroup,
    FormControlLabel,
    Divider,
    Box,
    TextField,
    Button,
    CircularProgress,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    Autocomplete,
} from "@mui/material";
import { recipesService } from "../../../services/recipes.service";
import { Ingredient } from "../../../models/ingredient.model";
import { Add, Remove } from "@mui/icons-material";
import { RecipeQuantity, units } from "../../../models/recipe-quantity.model";

const EditProductPage: React.FC = () => {
    const { product, ingredients } = useLoaderData() as { product: Recipe; ingredients: Ingredient[] };
    const navigate = useNavigate();
    const [formData, setFormData] = useState<Recipe>(product);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const newUnitRef = useRef<HTMLSelectElement>(null);
    const newIngredientRef = useRef<string>('');
    const newQuantityRef = useRef<HTMLInputElement>(null);

    const handlerSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const updatedRecipe = syncCategoriesWithFlags(formData);
            updatedRecipe.quantities = updatedRecipe.quantities.map(q => ({
                ingredient: q.ingredient,
                is_main: q.is_main,
                quantity: q.quantity,
                extra: q.extra,
                unit: q.unit
            }));
            await recipesService.update(updatedRecipe);
            alert("Producto actualizado con éxito.");
            navigate("/productos");
        } catch (error) {
            console.error(error);
            alert("Error al actualizar el producto.");
        } finally {
            setLoading(false);
        }
    };

    if (!product) {
        return <div>No product found</div>;
    }

    const syncCategoriesWithFlags = (recipe: Recipe): Recipe => {
        const categoryMap: { key: keyof Recipe; spanish: string; english: string }[] = [
            { key: "is_gluten_free", spanish: "libre de gluten", english: "gluten-free" },
            { key: "is_keto", spanish: "keto", english: "keto" },
            { key: "is_soy_free", spanish: "libre de soya", english: "soy-free" },
            { key: "is_lactose_free", spanish: "sin lactosa", english: "no lactose" },
            { key: "is_low_calories", spanish: "baja en calorías", english: "low calorie" },
            { key: "is_low_sodium", spanish: "bajo en sodio", english: "low sodium" },
            { key: "is_sugar_free", spanish: "baja azúcar", english: "sugar-free" },
            { key: "is_vegan", spanish: "vegano", english: "vegan" },
            { key: "is_veggie", spanish: "vegetariano", english: "vegetarian" },
            { key: "is_snack", spanish: "snack", english: "snack" },
        ];

        const currentSpanish = recipe.categories.spanish
            ?.split(",")
            .map(c => c.trim())
            .filter(Boolean) ?? [];

        const currentEnglish = recipe.categories.english
            ?.split(",")
            .map(c => c.trim())
            .filter(Boolean) ?? [];

        const updatedSpanish = new Set(currentSpanish);
        const updatedEnglish = new Set(currentEnglish);

        categoryMap.forEach(({ key, spanish, english }) => {
            if (recipe[key]) {
                updatedSpanish.add(spanish);
                updatedEnglish.add(english);
            } else {
                updatedSpanish.delete(spanish);
                updatedEnglish.delete(english);
            }
        });

        return {
            ...recipe,
            categories: {
                spanish: Array.from(updatedSpanish).join(", "),
                english: Array.from(updatedEnglish).join(", "),
            },
        };
    };

    const handleCheckboxChange = (field: keyof Recipe) => {
        setFormData((prev) => {
            const updated = {
                ...prev,
                [field]: !prev[field],
            };
            return syncCategoriesWithFlags(updated);
        });
    };

    const handleDescriptionChange = (lang: "spanish" | "english", value: string) => {
        setFormData((prev) => ({
            ...prev,
            description: {
                ...prev.description,
                [lang]: value,
            },
        }));
    };

    const handleStepChange = (index: number, lang: "spanish" | "english", value: string) => {
        setFormData(prev => {
            const updatedSteps = [...(prev.steps || [])];
            updatedSteps[index] = {
                ...updatedSteps[index],
                description: {
                    ...updatedSteps[index].description,
                    [lang]: value,
                },
            };
            return {
                ...prev,
                steps: updatedSteps,
            };
        });
    };

    const otherBooleans: { label: string; key: keyof Recipe }[] = [
        { label: "Sin gluten", key: "is_gluten_free" },
        { label: "Keto", key: "is_keto" },
        { label: "Sin soya", key: "is_soy_free" },
        { label: "Sin lactosa", key: "is_lactose_free" },
        { label: "Bajo en calorías", key: "is_low_calories" },
        { label: "Bajo sodio", key: "is_low_sodium" },
        { label: "Baja azúcar", key: "is_sugar_free" },
        { label: "Vegano", key: "is_vegan" },
        { label: "Vegetariano", key: "is_veggie" },
        { label: "Snack", key: "is_snack" },
    ];

    function removeQuantity(index: number): void {
        if (window.confirm("¿Estás seguro de que deseas eliminar este ingrediente?"))
            setFormData((prev) => {
                const updatedQuantities = [...(prev.quantities || [])];
                updatedQuantities.splice(index, 1);
                return {
                    ...prev,
                    quantities: updatedQuantities,
                };
            });
    }

    return (
        <Box maxWidth={600} mx="auto" p={2} component="form" onSubmit={handlerSubmit}>
            <Typography variant="h4" gutterBottom>
                Editar <strong>"{formData.name?.spanish}"</strong>
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
                ID del producto: <strong>{formData.id}</strong>
            </Typography>

            {/* Principal */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                        Disponibilidad
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.is_cooked_available}
                                onChange={() => handleCheckboxChange("is_cooked_available")}
                            />
                        }
                        label="¿Disponible como cocinada?"
                    />
                </CardContent>
            </Card>

            {/* Descripción editable */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                        Descripción
                    </Typography>
                    <TextField
                        label="Descripción en español"
                        multiline
                        minRows={3}
                        fullWidth
                        value={formData.description?.spanish || ""}
                        onChange={(e) => handleDescriptionChange("spanish", e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Descripción en inglés"
                        multiline
                        minRows={3}
                        fullWidth
                        value={formData.description?.english || ""}
                        onChange={(e) => handleDescriptionChange("english", e.target.value)}
                    />
                </CardContent>
            </Card>

            {/* Atributos nutricionales */}
            <Card>
                <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                        Características nutricionales
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <FormGroup>
                        {otherBooleans.map(({ label, key }) => (
                            <FormControlLabel
                                key={key}
                                control={
                                    <Switch
                                        checked={formData[key] as boolean}
                                        onChange={() => handleCheckboxChange(key)}
                                    />
                                }
                                label={label}
                            />
                        ))}
                    </FormGroup>
                </CardContent>
            </Card>

            {/* Mostrar categorías actuales */}
            <Box mt={3}>
                <Typography variant="subtitle2" gutterBottom>
                    Categorías (español):
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {formData.categories.spanish || "(ninguna)"}
                </Typography>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Categorías (inglés):
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {formData.categories.english || "(none)"}
                </Typography>
            </Box>

            {/* Lista de ingredientes */}
            {formData.quantities?.length > 0 && (
                <Card sx={{ mt: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Ingredientes <IconButton onClick={() => setShowModal(true)}><Add /></IconButton></Typography>
                        {formData.quantities.map((q, index) => (
                            <Box key={`${index}-${q.ingredient.replace(/ /g, '_')}`} mb={1}>
                                <Typography variant="body2">
                                    - {q.quantity} {q.unit?.spanish || q.unit?.english || ""} de <strong>{q.ingredient}</strong> <IconButton type="button" size="small" onClick={() => removeQuantity(index)}><Remove /></IconButton>
                                </Typography>
                            </Box>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Lista de pasos */}
            {formData.steps?.length > 0 && (
                <Card sx={{ mt: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Pasos de preparación</Typography>
                        {formData.steps.map((step, index) => (
                            <Box key={index} mb={3}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Paso {step.step}
                                </Typography>
                                <TextField
                                    label="Descripción en español"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={step.description.spanish || ""}
                                    onChange={(e) => handleStepChange(index, "spanish", e.target.value)}
                                    sx={{ mb: 1 }}
                                />
                                <TextField
                                    label="Descripción en inglés"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={step.description.english || ""}
                                    onChange={(e) => handleStepChange(index, "english", e.target.value)}
                                />
                            </Box>
                        ))}
                    </CardContent>
                </Card>
            )}


            <Box mt={3} textAlign="center">
                <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {loading ? "Guardando..." : "Guardar cambios"}
                </Button>
            </Box>

            <Dialog open={showModal} onClose={() => setShowModal(false)}>
                <DialogTitle>Agregar Ingrediente</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Cantidad"
                        type="text"
                        fullWidth
                        margin="normal"
                        inputRef={newQuantityRef}
                    />
                    <Select
                        native
                        defaultValue={JSON.stringify({ english: "u", spanish: "u" })}
                        fullWidth
                        inputProps={{ name: "unit", id: "unit-select" }}
                        inputRef={newUnitRef}
                        >
                        {units.map((unit) => (
                            <option
                            key={unit.spanish}
                            value={JSON.stringify({ english: unit.english, spanish: unit.spanish })}
                            >
                            {unit.spanish}
                            </option>
                        ))}
                    </Select>
                    <Autocomplete
                        options={ingredients}
                        getOptionLabel={(option) => option.name.spanish || ""}
                        filterOptions={(options, state) =>
                            options.filter((option) =>
                            option.name.spanish?.toLowerCase().includes(state.inputValue?.toLowerCase())
                            )
                        }
                        isOptionEqualToValue={(option, value) =>
                            option.name.english === value.name.english
                        }
                        renderInput={(params) => <TextField {...params} label="Ingrediente" />}
                        onChange={(_, value) => {
                            (newIngredientRef.current as any) = value?.name.english ?? "";
                        }}
                        disableClearable
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowModal(false)} color="error">Cancelar</Button>
                    <Button
                        onClick={async () => {
                            const selectedUnit = newUnitRef.current?.value;
                            const selectedIngredient = newIngredientRef.current;
                            const selectedQty = newQuantityRef.current?.value;
                            
                            if (!selectedUnit || !selectedIngredient || !selectedQty) return 
                            
                            const unitObj = JSON.parse(selectedUnit);

                            const newQuantity = new RecipeQuantity(
                                selectedIngredient,
                                false,
                                selectedQty,
                                null,
                                unitObj
                            )
                            setFormData((prev) => ({
                                ...prev,
                                quantities: [...(prev.quantities || []), newQuantity],
                            }));
                            setShowModal(false)
                        }}
                    >
                        Agregar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EditProductPage;